import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  IAccountCredit,
  IArraySorter,
  IBrand,
  IInvoice,
  IManualTransactionType,
  IPatient,
  IStaffer,
  ITransaction,
  TransactionProvider,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  IIdentifiable,
  INamedDocument,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  WithRef,
  safeCombineLatest,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ItemCodeResourceMapType } from '../../mappings/item-codes-to-xlsx';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';
import { PatientIdFilter } from '../filters/patient-id-filter';

export const PATIENT_TRANSACTION_RESOURCE_TYPE = 'patientTransaction';
export const PATIENT_INVOICE_RESOURCE_TYPE = 'patientInvoices';

export const PATIENT_INVOICE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_INVOICE_RESOURCE_TYPE,
    label: 'Patient Invoices',
    description: ``,
  },
});

export function buildExtendedData<Source extends ISourceEntityTypes>(
  payment: IGetRecordResponse<Source>,
  provider: TransactionProvider,
  providerSubType?: INamedDocument<IManualTransactionType>
): object {
  const extendedData = {
    payment: payment.data,
    transactionType: providerSubType,
  };

  if (provider === TransactionProvider.AccountCredit) {
    return {
      ...extendedData,
      accountCreditsUsed: [],
    };
  }

  return extendedData;
}

export interface IInvoiceUpdateData {
  invoiceRef: DocumentReference<IInvoice>;
  transactionRefs: DocumentReference<ITransaction>[];
  accountCreditRefs: DocumentReference<IAccountCredit>[];
}

export interface IInvoiceBuildData {
  invoice: IIdentifiable & IInvoice;
  transactions: (IIdentifiable & ITransaction)[];
}

export interface IPatientInvoiceMigrationData {
  patientRef: DocumentReference<IPatient>;
  data: IInvoiceBuildData[];
  createdAt: Timestamp;
}

export interface IPatientInvoiceDestinationRecord<
  PatientRecord extends ISourceEntityTypes,
> {
  sourceRef: PrimaryKey<ISourceEntityRecord<PatientRecord>>;
  invoiceData: {
    invoiceRef: DocumentReference<IInvoice>;
    transactionRefs: DocumentReference<ITransaction>[];
  }[];
}

export interface IPatientInvoiceJobData<
  PatientRecord extends ISourceEntityTypes,
> {
  sourcePatient: IGetRecordResponse<PatientRecord>;
  brand: INamedDocument<IBrand>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: INamedDocument<IStaffer>[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
}

export abstract class BasePatientInvoiceDestinationEntity<
  PatientRecord extends ISourceEntityTypes,
  JobData extends IPatientInvoiceJobData<PatientRecord>,
> extends BaseDestinationEntity<
  IPatientInvoiceDestinationRecord<PatientRecord>,
  JobData,
  IPatientInvoiceMigrationData
> {
  destinationEntity = PATIENT_INVOICE_DESTINATION_ENTITY;

  abstract patientSourceEntity: ISourceEntityHandler<PatientRecord>;

  override sorters: IArraySorter[] = [
    {
      key: 'invoice',
      sortByPath: ['reference', 'issuedAt.timestampValue'],
    },
    {
      key: 'transactions',
      sortByPath: 'reference',
    },
  ];

  get sourceCountComparison(): ISourceEntityHandler<PatientRecord> {
    return this.patientSourceEntity;
  }

  override filters = [
    new PatientIdFilter<JobData>((data) =>
      this.patientSourceEntity
        .getSourceRecordId(data.sourcePatient.data)
        .toString()
    ),
  ];

  sourceCountDataAccessor(data: JobData): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: JobData): string {
    return data.sourcePatient.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<
      IPatientInvoiceDestinationRecord<PatientRecord>
    >
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return safeCombineLatest(
      record.data.invoiceData.map((data) =>
        combineLatest([
          Firestore.getDoc(data.invoiceRef),
          safeCombineLatest(
            data.transactionRefs.map((transactionRef) =>
              Firestore.getDoc(transactionRef)
            )
          ),
        ])
      )
    ).pipe(
      map(([invoiceRef, transactions]) => {
        const data: IMigratedDataSummary[] = [
          {
            label: 'Invoice',
            data: invoiceRef,
          },
        ];

        data.push(
          ...transactions.map((transaction) => ({
            label: 'Transactions',
            data: transaction,
          }))
        );

        return data;
      })
    );
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: JobData,
    _migrationData: IPatientInvoiceMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<PatientRecord>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage: errorMessage ?? 'Missing required properties for patient',
      failData: {
        patientRef: patient.id,
      },
    };
  }

  buildSkippedResponse(
    data: JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  protected _buildSuccessResponse(
    patient: IGetRecordResponse<PatientRecord>,
    updateData?: IInvoiceUpdateData[]
  ): IDestinationEntityRecord<IPatientInvoiceDestinationRecord<PatientRecord>> {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      data: {
        sourceRef: patient.record.id,
        invoiceData: updateData ?? [],
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
